/**
 * Map a viewport rectangle onto a captureVisibleTab bitmap.
 * The shot is the visible tab, so its pixel size is the viewport times the
 * capture scale (usually devicePixelRatio, but measured from the bitmap).
 * @param {{ left: number, top: number, width: number, height: number }} rect
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @param {number} viewportWidth
 * @param {number} viewportHeight
 */
export function cropRectToImagePixels(rect, imageWidth, imageHeight, viewportWidth, viewportHeight) {
  const viewW = Number(viewportWidth) || 1;
  const viewH = Number(viewportHeight) || 1;
  const scaleX = Number(imageWidth) / viewW;
  const scaleY = Number(imageHeight) / viewH;
  const sx = Math.max(0, Math.round(Number(rect?.left) * scaleX));
  const sy = Math.max(0, Math.round(Number(rect?.top) * scaleY));
  const sw = Math.max(1, Math.min(
    Math.round(Number(imageWidth) - sx),
    Math.round(Number(rect?.width) * scaleX),
  ));
  const sh = Math.max(1, Math.min(
    Math.round(Number(imageHeight) - sy),
    Math.round(Number(rect?.height) * scaleY),
  ));
  return { sx, sy, sw, sh };
}

export function captureVisibleTab(extensionId) {
  return new Promise((resolve, reject) => {
    const runtime = typeof window !== 'undefined' ? window.chrome?.runtime : null;
    if (!extensionId || typeof runtime?.sendMessage !== 'function') {
      reject(new Error('extension unavailable'));
      return;
    }
    runtime.sendMessage(extensionId, { type: 'CAPTURE_SCREENSHOT' }, response => {
      const runtimeError = runtime.lastError;
      if (runtimeError) {
        reject(new Error(runtimeError.message));
        return;
      }
      if (!response?.dataUrl) {
        reject(new Error(response?.error || 'empty capture'));
        return;
      }
      resolve(response.dataUrl);
    });
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('capture image failed'));
    image.src = src;
  });
}

/**
 * Crop the visible-tab screenshot to an on-screen element box.
 * @param {string} extensionId
 * @param {{ left: number, top: number, width: number, height: number }} rect
 * @returns {Promise<string>}
 */
export async function captureElementJpeg(extensionId, rect) {
  const dataUrl = await captureVisibleTab(extensionId);
  const image = await loadImage(dataUrl);
  const { sx, sy, sw, sh } = cropRectToImagePixels(
    rect,
    image.width,
    image.height,
    window.innerWidth,
    window.innerHeight,
  );
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas unavailable');
  context.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh);
  return canvas.toDataURL('image/jpeg', 0.92);
}
